import { useCallback } from 'react';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import { getActiveOrgId } from '@/lib/activeOrg';
import ceoxOrgStackService from '@/services/ceoxOrgStack';
import { OrgStack, StackCategory, StackProvider } from '@/types/api/ceoxStack';

type UseOrgStackResult = {
  stack: OrgStack | null;
  isLoading: boolean;
  hasStack: boolean;
  isProviderActive: (category: StackCategory) => boolean;
  getProvider: (category: StackCategory) => StackProvider | null;
  saveStack: (entries: { category: StackCategory; provider: StackProvider }[]) => Promise<void>;
  updateProvider: (category: StackCategory, provider: StackProvider) => Promise<void>;
  refetch: () => void;
};

export function useOrgStack(): UseOrgStackResult {
  const orgId = getActiveOrgId();

  const { data: stack, isLoading, refetch } = useSeoFetch<OrgStack>(
    ['ceox-org-stack', orgId],
    () => ceoxOrgStackService.getOrgStack(),
    { enabled: !!orgId }
  );

  const hasStack = !!stack && Object.values(stack).some((entry) => entry !== null);

  const isProviderActive = useCallback(
    (category: StackCategory): boolean => {
      if (!stack) return false;
      const entry = stack[category];
      return !!entry && entry.provider !== 'none' && entry.isActive;
    },
    [stack]
  );

  const getProvider = useCallback(
    (category: StackCategory): StackProvider | null => {
      if (!stack) return null;
      return stack[category]?.provider ?? null;
    },
    [stack]
  );

  const saveStack = useCallback(
    async (entries: { category: StackCategory; provider: StackProvider }[]) => {
      await ceoxOrgStackService.saveStack(entries);
      refetch();
    },
    [refetch]
  );

  const updateProvider = useCallback(
    async (category: StackCategory, provider: StackProvider) => {
      await ceoxOrgStackService.updateProvider(category, provider);
      refetch();
    },
    [refetch]
  );

  return {
    stack: stack ?? null,
    isLoading,
    hasStack,
    isProviderActive,
    getProvider,
    saveStack,
    updateProvider,
    refetch,
  };
}
